import { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Spinner,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { toast } from "react-toastify";

export const CardSiigoInvoice = ({ order, onOrderUpdate }) => {
  const { postData } = useDataFetch();
  const [retryingInvoice, setRetryingInvoice] = useState(false);

  const { mutateAsync: retrySiigoInvoiceMutation } = postData(
    `/order/retry-siigo-invoice/${order?.id}`,
    {},
    {
      onSuccess: (response) => {
        if (response.data?.order?.siigo_invoice_status === "success") {
          toast.success("Factura generada exitosamente");
        } else {
          toast.error(response.errors || "Error al generar factura");
        }
        onOrderUpdate?.(response.data?.order);
        setRetryingInvoice(false);
      },
      onError: (error) => {
        console.error("Error al reintentar factura:", error);
        toast.error("Error al generar factura. Intente nuevamente.");
        setRetryingInvoice(false);
      },
    }
  );

  if (!order || !order.is_paid) return null;

  const retrySiigoInvoice = async () => {
    setRetryingInvoice(true);
    await retrySiigoInvoiceMutation();
  };

  const renderGenerateButton = (color, icon, label) => (
    <Btn
      attrBtn={{
        color,
        size: "sm",
        onClick: retrySiigoInvoice,
        disabled: retryingInvoice,
        className: "d-flex align-items-center",
      }}
    >
      {retryingInvoice ? (
        <>
          <Spinner size="sm" className="me-1" />
          Generando...
        </>
      ) : (
        <>
          <i className={`fa ${icon} me-1`} />
          {label}
        </>
      )}
    </Btn>
  );

  const getSiigoStampStatus = () => {
    const stamp = order.siigo_invoice?.stamp?.status;
    if (!stamp) return null;

    const statusConfig = {
      'Draft': { color: 'secondary', label: 'Borrador' },
      'Pending': { color: 'warning', label: 'Pendiente' },
      'Sent': { color: 'info', label: 'Enviado' },
      'Accepted': { color: 'success', label: 'Aceptado' },
      'Rejected': { color: 'danger', label: 'Rechazado' },
    };

    return statusConfig[stamp] || { color: 'secondary', label: stamp };
  };

  return (
    <Card className="shadow-sm">
      <CardHeader tag="h5" className="d-flex justify-content-between align-items-center">
        <div>
          <i className="fa fa-file-invoice me-2" />
          Factura Electrónica
        </div>
        {order.siigo_invoice_status === 'success' && (
          <Badge style={{ color: '#fff' }} color="success" pill>
            <i className="fa fa-check me-1" />
            Generada
          </Badge>
        )}
        {order.siigo_invoice_status === 'error' && (
          <Badge style={{ color: '#fff' }} color="danger" pill>
            <i className="fa fa-exclamation-triangle me-1" />
            Error
          </Badge>
        )}
        {order.siigo_invoice_status === 'pending' && (
          <Badge style={{ color: '#fff' }} color="secondary" pill>
            <i className="fa fa-clock me-1" />
            Pendiente
          </Badge>
        )}
      </CardHeader>

      <CardBody>
        <Table borderless className="mb-0">
          <tbody>
            <tr>
              <th style={{ width: '40%' }}>Estado</th>
              <td>
                {order.siigo_invoice_status === 'success' && order.siigo_invoice ? (
                  <div className="d-flex flex-column gap-2">
                    <div>
                      <Badge color="success">Generada</Badge>
                      {order.siigo_invoice.name && (
                        <span className="ms-2 fw-bold">{order.siigo_invoice.name}</span>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      {order.siigo_invoice.pdf_url && (
                        <a
                          href={order.siigo_invoice.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          <i className="fa fa-file-pdf me-1" />
                          Descargar PDF
                        </a>
                      )}
                      {order.siigo_invoice.public_url && (
                        <a
                          href={order.siigo_invoice.public_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="fa fa-external-link-alt me-1" />
                          Ver en Siigo
                        </a>
                      )}
                    </div>
                  </div>
                ) : order.siigo_invoice_status === 'error' ? (
                  <div className="d-flex flex-column gap-2">
                    <Badge color="danger">Error al generar</Badge>
                    {order.siigo_invoice_error?.response?.Errors && (
                      <div className="alert alert-danger mb-2 py-2 px-3">
                        <small className="text-danger">
                          {order.siigo_invoice_error.response.Errors.map((err, idx) => (
                            <div key={idx}>
                              <i className="fa fa-exclamation-circle me-1" />
                              {err.Message}
                            </div>
                          ))}
                        </small>
                      </div>
                    )}
                    {renderGenerateButton("warning", "fa-redo", "Reintentar Factura")}
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    <Badge color="secondary">Pendiente de generación</Badge>
                    {renderGenerateButton("primary", "fa-file-invoice", "Generar Factura")}
                  </div>
                )}
              </td>
            </tr>

            {order.siigo_invoice?.id && (
              <tr>
                <th>ID Siigo</th>
                <td>
                  <code>{order.siigo_invoice.id}</code>
                </td>
              </tr>
            )}

            {order.siigo_invoice?.number && (
              <tr>
                <th>Número de Factura</th>
                <td className="fw-bold">{order.siigo_invoice.name}</td>
              </tr>
            )}

            {order.siigo_invoice?.stamp && (
              <tr>
                <th>Estado DIAN</th>
                <td>
                  <Badge color={getSiigoStampStatus()?.color}>
                    {getSiigoStampStatus()?.label}
                  </Badge>
                  {order.siigo_invoice.stamp.cufe && (
                    <div className="mt-1">
                      <small className="text-muted">
                        CUFE: <code className="text-muted">{order.siigo_invoice.stamp.cufe}</code>
                      </small>
                    </div>
                  )}
                </td>
              </tr>
            )}

            {order.siigo_invoice?.mail && (
              <tr>
                <th>Envío por Email</th>
                <td>
                  <Badge color={order.siigo_invoice.mail.status === 'sent' ? 'success' : 'warning'}>
                    {order.siigo_invoice.mail.status === 'sent' ? (
                      <>
                        <i className="fa fa-check me-1" />
                        Enviado
                      </>
                    ) : (
                      <>
                        <i className="fa fa-clock me-1" />
                        No enviado
                      </>
                    )}
                  </Badge>
                  {order.siigo_invoice.mail.observations && (
                    <div className="mt-1">
                      <small className="text-muted">{order.siigo_invoice.mail.observations}</small>
                    </div>
                  )}
                </td>
              </tr>
            )}

            {order.siigo_invoice?.metadata?.created && (
              <tr>
                <th>Fecha de Creación</th>
                <td>
                  <i className="fa fa-calendar me-2 text-muted" />
                  {new Date(order.siigo_invoice.metadata.created).toLocaleString('es-CO', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </td>
              </tr>
            )}

            {order.siigo_invoice_error?.timestamp && order.siigo_invoice_status === 'error' && (
              <tr>
                <th>Fecha del Error</th>
                <td>
                  <i className="fa fa-calendar me-2 text-muted" />
                  {new Date(order.siigo_invoice_error.timestamp).toLocaleString('es-CO', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
};
